import {
    BlockMarkup,
    BlockMarkupItem,
    BlockMarkupItemType, CodeMarkup,
    ImageMarkup,
    ListMarkup,
    MarkupTree,
    PlainTextMarkup,
    ReferenceRow,
    ReferenceSection
} from "./markup.interface";
import { SubCard } from "../api";
import {Parser} from "./parsers/parser";
import {imageParser} from "./parsers/imageParser";
import {codeParser} from "./parsers/codeParser";
import {exhaustiveFail} from "../utils/exhaustiveFail";
import {iconParser} from "./parsers/iconParser";
import {listParser} from "./parsers/listParser";

const markers = {
    question: '{question}',
    answer: '{answer}',
    hint: '{hint}',
    footer: '{footer}',
    examples: '{examples}',
    reference: '{reference}',
    tags: '{tags}',
    subcards: '{subcards}',
}

function pushTextIfNotEmpty(result: BlockMarkupItem[], text: string) {
    const trimmedText = text.trim();
    if (trimmedText.length === 0)
        return;

    const lines = trimmedText.split(/\r?\n/);
    for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine.length !== 0) {
            result.push({
                type: BlockMarkupItemType.PlainText,
                text: trimmedLine,
            })
        }
    }
}

function getMatchedParser(markup: string, startIndex: number, parsers: Parser[]): Parser | null {
    for (const parser of parsers) {
        if (parser.matches(markup, startIndex))
            return parser;
    }
    return null;
}

function parseBlock(markup: string): BlockMarkup {
    const result: BlockMarkup = [];
    const parsers: Parser[] = [
        codeParser,
        imageParser,
        iconParser,
        listParser,
    ];

    let cursor = 0;
    for (let i = 0; i < markup.length; i++) {
        const parser = getMatchedParser(markup, i, parsers);
        if (parser !== null) {
            pushTextIfNotEmpty(result, markup.substring(cursor, i - 1));

            const parseResult = parser.parse(markup, i);
            if (parseResult.item !== null) {
                result.push(parseResult.item);
            }

            cursor = parseResult.lastIndex + 1;
            i = parseResult.lastIndex;
        }
    }

    pushTextIfNotEmpty(result, markup.substring(cursor));

    return result;
}

function getNextMarkerIndex(startIndex: number, markup: string): number {
    const allMarkers = Object.values(markers);
    let minIndex: number | undefined = undefined;

    for (const marker of allMarkers) {
        const index = markup.indexOf(marker, startIndex);
        if (index === -1)
            continue;

        if (minIndex === undefined)
            minIndex = index;
        else
            minIndex = Math.min(minIndex, index);
    }

    return minIndex ?? -1;
}

function findAndParseBlock(markup: string, marker: string) {
    const index = markup.indexOf(marker);
    if (index === -1)
        return [];

    const nextMarkerIndex = getNextMarkerIndex(index + 1, markup);
    const endIndex = nextMarkerIndex !== -1
        ? nextMarkerIndex
        : undefined;

    return parseBlock(markup.substring(index + marker.length, endIndex));
}

function getQuestion(markup: string): BlockMarkup {
    const start = markup.trimStart().startsWith(markers.question)
        ? markup.indexOf(markers.question) + markers.question.length
        : 0;
    const nextMarkerIndex = getNextMarkerIndex(start > 0 ? start : 0, markup);
    const endIndex = nextMarkerIndex !== -1 ? nextMarkerIndex : undefined;
    return parseBlock(markup.substring(start, endIndex));
}

function getHint(markup: string): BlockMarkup {
    return findAndParseBlock(markup, markers.hint);
}

function getAnswer(markup: string): BlockMarkup {
    return findAndParseBlock(markup, markers.answer);
}

function getFooter(markup: string): BlockMarkup {
    return findAndParseBlock(markup, markers.footer);
}

function getExamples(markup: string): BlockMarkup {
    return findAndParseBlock(markup, markers.examples);
}

function parseReferenceSections(content: string): ReferenceSection[] {
    const sections: ReferenceSection[] = [];

    for (const rawSection of content.split('===')) {
        const lines = rawSection
            .split(/\r?\n/)
            .map(line => line.trim())
            .filter(line => line.length > 0);

        if (lines.length === 0)
            continue;

        const [title, ...rowLines] = lines;
        let header: string[] | null = null;
        const rows: ReferenceRow[] = [];

        for (const line of rowLines) {
            if (line.startsWith('|') && line.endsWith('|')) {
                header = line.slice(1, -1).split('|').map(c => c.trim()).filter(c => c.length > 0);
            } else {
                const highlight = line.startsWith('!');
                const rawCells = highlight ? line.slice(1) : line;
                const cells = rawCells.split('|').map(c => c.trim());
                rows.push({ cells, highlight });
            }
        }

        sections.push({ title, header, rows });
    }

    return sections;
}

function getReference(markup: string): ReferenceSection[] {
    const index = markup.indexOf(markers.reference);
    if (index === -1)
        return [];

    const nextMarkerIndex = getNextMarkerIndex(index + 1, markup);
    const endIndex = nextMarkerIndex !== -1
        ? nextMarkerIndex
        : undefined;

    return parseReferenceSections(markup.substring(index + markers.reference.length, endIndex));
}

function getTags(markup: string): string[] {
    const tagsIndex = markup.indexOf(markers.tags);
    if (tagsIndex === -1)
        return [];

    const nextMarkerIndex = getNextMarkerIndex(tagsIndex + 1, markup);
    const endIndex = nextMarkerIndex !== -1 ? nextMarkerIndex : undefined;

    const tagsContent = markup.substring(tagsIndex + markers.tags.length, endIndex).trim();
    
    // Если после маркера {tags} нет содержимого, возвращаем пустой массив
    if (tagsContent.length === 0)
        return [];
        
    return tagsContent
        .split(',')
        .map(str => str.trim())
        .filter(tag => tag.length > 0); // Дополнительная фильтрация пустых тегов
}

function getIcon(markup: string): string {
    const match = markup.match(/{icon-([\w\d]*)}/);
    if (match === null)
        return '';

    return match[1];
}

interface SubCardsData {
    subCards: SubCard[];
}

function extractSubCardGroup(question: string): { question: string; group?: string } {
    const lines = question.split('\n');
    const firstLine = lines[0].trim();

    if (firstLine.startsWith('@') && firstLine.length > 1) {
        const group = firstLine.slice(1).trim();
        if (group) {
            return { question: lines.slice(1).join('\n').trim(), group };
        }
    }

    return { question };
}

function parseSubCardsArray(content: string): SubCard[] {
    try {
        const sections = content.split('===').map(s => s.trim());
        const subCards: SubCard[] = [];

        for (const section of sections) {
            if (!section) continue;

            const parts = section.split('---').map(s => s.trim());
            if (parts.length >= 2 && parts[0] && parts[1]) {
                const { question, group } = extractSubCardGroup(parts[0]);
                if (!question) continue;
                subCards.push(group !== undefined
                    ? { question, answer: parts[1], group }
                    : { question, answer: parts[1] });
            }
            // Silently ignore invalid subcards
        }
        
        return subCards;
    } catch (error) {
        // Return empty array on any parsing error
        // eslint-disable-next-line no-console
        console.warn('Error parsing subcards:', error);
        return [];
    }
}

function parseSubCards(content: string): SubCardsData {
    // Accept an optional count, e.g. {subcards} or {subcards 2}; the number is ignored.
    const subCardsMatch = content.match(/\{subcards(?:\s+\d+)?\}([\s\S]*?)(?=\{|$)/);
    
    if (!subCardsMatch) {
        return { subCards: [] };
    }
    
    const subCardsContent = subCardsMatch[1].trim();
    const subCards = parseSubCardsArray(subCardsContent);
    
    return {
        subCards
    };
}

function formatBlockMarkupItem(item: BlockMarkupItem): string {
    const type = item.type;
    let textItem: PlainTextMarkup;
    let imageItem: ImageMarkup;
    let codeItem: CodeMarkup;
    let listItem: ListMarkup;

    switch (type) {
        case BlockMarkupItemType.PlainText:
            textItem = item as PlainTextMarkup;
            return textItem.text;
        case BlockMarkupItemType.Image:
            imageItem = item as ImageMarkup;
            return `{image-${imageItem.imageId}}`;
        case BlockMarkupItemType.Code:
            codeItem = item as CodeMarkup;
            return '{code}\n' +
                codeItem.code + '\n' +
                '{code}';
        case BlockMarkupItemType.List:
            listItem = item as ListMarkup;
            return listItem.ordered
                ? listItem.items.map((t, i) => `${i + 1}. ${t}`).join('\n')
                : listItem.items.map(t => `- ${t}`).join('\n');
        default:
            exhaustiveFail(type);
    }
}

function formatIcon(icon: string): string {
    if (icon === '')
        return '';

    return `{icon-${icon}}`;
}

function formatBlock(block: BlockMarkup): string {
    let result = '';
    for (const item of block) {
        result = result + formatBlockMarkupItem(item) + '\n';
    }
    return result.trim();
}

function formatTags(tags: string[]): string {
    return tags.join(', ');
}

function formatSubCards(subCards: SubCard[]): string {
    return subCards.map(subCard => {
        const question = subCard.group !== undefined && subCard.group.length > 0
            ? `@${subCard.group}\n${subCard.question}`
            : subCard.question;
        return `${question}\n---\n${subCard.answer}`;
    }).join('\n===\n');
}

function formatReference(sections: ReferenceSection[]): string {
    return sections.map(section => {
        const lines = [section.title];
        if (section.header !== null)
            lines.push('| ' + section.header.join(' | ') + ' |');
        for (const row of section.rows) {
            const prefix = row.highlight ? '!' : '';
            lines.push(prefix + row.cells.join(' | '));
        }
        return lines.join('\n');
    }).join('\n===\n');
}

function validate(tree: MarkupTree): string {
    if (tree.question.length === 0)
        return 'Question is empty';

    if (tree.answer.length === 0)
        return 'Answer is empty';

    return '';
}

export const markupFormatter = {
    parseBlock,
    parse: (markup: string) => {
        try {
            const result = new MarkupTree();
            result.icon = getIcon(markup);
            result.question = getQuestion(markup);
            result.hint = getHint(markup);
            result.answer = getAnswer(markup);
            result.examples = getExamples(markup);
            result.reference = getReference(markup);
            result.footer = getFooter(markup);
            result.tags = getTags(markup);
            
            // Parse subcards section
            const subCardsData = parseSubCards(markup);
            result.subCards = subCardsData.subCards;
            result.hasSubCards = subCardsData.subCards.length > 0;
            
            result.error = validate(result);

            return result;
        } catch {
            return new MarkupTree();
        }
    },

    format: (tree: MarkupTree) => {
        let result = formatIcon(tree.icon);

        if (result !== '') {
            result += '\n';
        }

        result += formatBlock(tree.question);

        if (tree.hint.length > 0) {
            result += '\n' + markers.hint +
                '\n' + formatBlock(tree.hint);
        }

        result += '\n' + markers.answer +
            '\n' + formatBlock(tree.answer);

        if (tree.reference.length > 0) {
            result += '\n' + markers.reference +
                '\n' + formatReference(tree.reference);
        }

        if (tree.examples.length > 0) {
            result += '\n' + markers.examples +
                '\n' + formatBlock(tree.examples);
        }

        if (tree.footer.length > 0) {
            result += '\n' + markers.footer +
                '\n' + formatBlock(tree.footer);
        }

        if (tree.tags.length > 0) {
            result += '\n' + markers.tags +
                '\n' + formatTags(tree.tags);
        }

        if (tree.subCards.length > 0) {
            result += '\n' + markers.subcards +
                '\n' + formatSubCards(tree.subCards);
        }

        return result;
    }
};
